import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IncomeAndAssetsSteps } from '../../../../../../enums/income-and-assets-steps.enum';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { StepperContextService } from '../../../../../../services/stepper-context.service';
import { StepperAnswersService } from '../../../../../../services/stepper-answers.service';
import { StepperTasksService } from '../../../../../../services/stepper-tasks.service';
import { StepSection } from '../../../../../../enums/step-section.enum';
import { SubmitSteps } from '../../../../../../enums/submit-steps.enum';

export enum OtherIncomeSourceOptions {
  Retirement = 'Retirement Income',
  SocialSecurity = 'Social Security',
  Asset = 'Asset Utilization',
  BankStatements = 'Bank Statements',
  SupportIncome = 'Support Income'
}

@Component({
  selector: 'lgv-other-income-source',
  standalone: true,
  imports: [CommonModule,
    MatDividerModule, MatButtonModule
  ],
  templateUrl: './other-income-source.component.html',
  styleUrls: ['./other-income-source.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtherIncomeSourceComponent {
  OtherIncomeSourceOptions = OtherIncomeSourceOptions
  optionSelected = this.answerService.getAnswer(IncomeAndAssetsSteps.OtherIncomeSource) ?? []

  @Output() stepChange = new EventEmitter<any>();
  @Output() sectionChange = new EventEmitter<any>();

  private retirementTasks: string[] = [
    "Asset Statements: 401k, IRA, Retirement accounts",
    "Retirement/Pension Letter"
  ];

  private socialSecurityTasks: string[] = [
    "Social Security Awards Letter"
  ];

  private assetTasks: string[] = [
    "Asset Statements: 401k, IRA, Retirement accounts",
    "Saving - Bank Statements",
    "Checking - Bank Statements",
  ];

  constructor(
    public contextService: StepperContextService,
    public answerService: StepperAnswersService,
    public taskService: StepperTasksService
  ) { }

  ngOnInit(): void {
    this.syncTasksWithAnswer(this.optionSelected);
  }

  onAnswerSelected(ans: string) {
    const index = this.optionSelected.indexOf(ans);
    if (index === -1) {
      // Not selected yet, add it
      this.optionSelected.push(ans);
    } else {
      // Already selected, remove it
      this.optionSelected.splice(index, 1);
    }
  }

  onNext() {
    this.answerService.setAnswer(IncomeAndAssetsSteps.OtherIncomeSource, this.optionSelected)
    this.clearTasks()
    this.syncTasksWithAnswer(this.optionSelected)
    if (!this.optionSelected.includes(OtherIncomeSourceOptions.BankStatements)) {
      this.answerService.setAnswer(IncomeAndAssetsSteps.OtherBankStatements, '')
    }
    if (!this.optionSelected.includes(OtherIncomeSourceOptions.SupportIncome)) {
      this.answerService.setAnswer(IncomeAndAssetsSteps.OtherSupportIncome, '')
    }
    this.optionSelected.includes(OtherIncomeSourceOptions.BankStatements) ? this.goToStep(IncomeAndAssetsSteps.OtherBankStatements)
      : (this.optionSelected.includes(OtherIncomeSourceOptions.SupportIncome) ? this.goToStep(IncomeAndAssetsSteps.OtherSupportIncome)
        : this.goToSection(StepSection.Submit, SubmitSteps.Confirmation))
  }

  onBack() {
    this.answerService.setAnswer(IncomeAndAssetsSteps.OtherIncomeSource, this.optionSelected)
    this.clearTasks()
    this.goToStep(IncomeAndAssetsSteps.IncomeSource)
  }

  goToStep(step) {
    this.stepChange.emit(step);
  }

  goToSection(section, step) {
    this.sectionChange.emit([section, step]);
  }

  isBtnSelected(option: string) {
    return this.optionSelected.includes(option);
  }

  isNextDisabled(): boolean {
    if (this.optionSelected.length === 0) {
      return true
    }
    return false
  }

  private clearTasks(): void {
    [...this.retirementTasks, ...this.socialSecurityTasks, ...this.assetTasks]
      .forEach(task => this.taskService.removeTask(task));
  }

  private syncTasksWithAnswer(answers: string[]): void {
    if (!Array.isArray(answers)) return;

    this.clearTasks(); // Always clear previous tasks before applying new ones

    if (answers.includes(OtherIncomeSourceOptions.Retirement)) {
      this.retirementTasks.forEach(task => this.taskService.addTask(task));
    }

    if (answers.includes(OtherIncomeSourceOptions.SocialSecurity)) {
      this.socialSecurityTasks.forEach(task => this.taskService.addTask(task));
    }

    if (answers.includes(OtherIncomeSourceOptions.Asset)) {
      this.assetTasks.forEach(task => this.taskService.addTask(task));
    }
  }
}
